// src/components/UserManager.jsx  (Phase 2 — Supabase Auth)
import React, { useState, useEffect } from 'react'
import { useApp } from '../context/AppContext'
import { Modal, ConfirmModal } from './UI'
import { ACCOUNT_COLORS } from '../utils/constants'
import { initials as mkInitials } from '../utils/helpers'
import { supabase }  from '../lib/supabase'
import { signUp, listProfiles } from '../services/authService'

const emptyForm = () => ({ name: '', email: '', password: '', role: 'user', color: '#1c3a5e' })

export default function UserManager({ open, onClose }) {
  const { currentProfile, toast, toastError } = useApp()
  const [profiles,    setProfiles]    = useState([])
  const [form,        setForm]        = useState(emptyForm())
  const [loading,     setLoading]     = useState(false)
  const [confirmId,   setConfirmId]   = useState(null)

  useEffect(() => {
    if (open) loadProfiles()
  }, [open])

  const loadProfiles = async () => {
    const { data } = await listProfiles()
    setProfiles(data || [])
  }

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password) {
      toastError('Fill in all fields'); return
    }
    if (form.password.length < 6) { toastError('Password must be at least 6 characters'); return }
    if (profiles.find(p => p.email === form.email.toLowerCase())) {
      toastError('Email already registered'); return
    }
    setLoading(true)
    const { error } = await signUp(form.email.trim().toLowerCase(), form.password, {
      name:     form.name.trim(),
      role:     form.role,
      color:    form.color,
      initials: mkInitials(form.name.trim()),
    })
    setLoading(false)
    if (error) { toastError('Failed to create user: ' + error); return }
    toast('User created! They can now sign in.')
    setForm(emptyForm())
    await loadProfiles()
  }

  const removeUser = async (id) => {
    // Delete from auth.users (cascades to profiles via FK)
    const { error } = await supabase.auth.admin.deleteUser(id)
    if (error) {
      // Fallback: just delete profile if admin API not available on client
      await supabase.from('profiles').delete().eq('id', id)
    }
    await loadProfiles()
    toast('User removed')
  }

  return (
    <>
      <Modal open={open} onClose={onClose} title="Manage users" subtitle="Add or remove TecFin accounts">
        {/* User list */}
        <div style={{ marginBottom: 16 }}>
          {profiles.map(p => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px', borderRadius: 8, border: '1px solid var(--border)', marginBottom: 8, background: 'var(--surface-alt)' }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                {p.initials}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{p.name}</div>
                <div style={{ fontSize: 11, color: 'var(--gray-600)', marginTop: 2 }}>{p.role}</div>
              </div>
              <span className={`badge badge-${p.role}`}>{p.role}</span>
              {p.id !== currentProfile?.id ? (
                <button className="icon-btn danger" onClick={() => setConfirmId(p.id)} aria-label={`Remove ${p.name}`}>
                  <i className="ti ti-trash" aria-hidden="true" />
                </button>
              ) : (
                <span style={{ width: 30 }} />
              )}
            </div>
          ))}
          {profiles.length === 0 && (
            <p style={{ color: 'var(--gray-400)', fontSize: 13, textAlign: 'center', padding: '12px 0' }}>No users found</p>
          )}
        </div>

        {/* Add user */}
        <div style={{ background: 'var(--surface-alt)', border: '1px solid var(--border)', borderRadius: 10, padding: 14 }}>
          <div className="card-title" style={{ marginBottom: 12 }}>
            <i className="ti ti-user-plus" aria-hidden="true" /> Add new user
          </div>
          <div className="form-grid">
            <div className="field">
              <label className="field-label">Full name</label>
              <input className="input" value={form.name} onChange={e => upd('name', e.target.value)} placeholder="e.g. Priya Sharma" />
            </div>
            <div className="field">
              <label className="field-label">Email</label>
              <input className="input" type="email" value={form.email} onChange={e => upd('email', e.target.value)} placeholder="priya@example.com" />
            </div>
            <div className="field">
              <label className="field-label">Password</label>
              <input className="input" type="password" value={form.password} onChange={e => upd('password', e.target.value)} placeholder="min 6 characters" />
            </div>
            <div className="field">
              <label className="field-label">Role</label>
              <select className="select" value={form.role} onChange={e => upd('role', e.target.value)}>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          <div style={{ marginTop: 10 }}>
            <label className="field-label" style={{ display: 'block', marginBottom: 7 }}>Avatar color</label>
            <div style={{ display: 'flex', gap: 7 }}>
              {ACCOUNT_COLORS.slice(0, 6).map(c => (
                <div key={c} onClick={() => upd('color', c)}
                  style={{ width: 22, height: 22, borderRadius: '50%', background: c, cursor: 'pointer', border: `3px solid ${form.color === c ? 'var(--gray-900)' : 'transparent'}` }} />
              ))}
            </div>
          </div>
          <div className="btn-row">
            <button className="btn btn-primary" onClick={submit} disabled={loading}>
              <i className="ti ti-plus" aria-hidden="true" />
              {loading ? 'Creating…' : 'Add user'}
            </button>
            <button className="btn" onClick={() => setForm(emptyForm())}>Clear</button>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={!!confirmId}
        onClose={() => setConfirmId(null)}
        onConfirm={() => removeUser(confirmId)}
        title="Remove user"
        message="This will permanently remove the user and all their data."
        confirmLabel="Remove"
      />
    </>
  )
}
